import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { PSYCHOLOGICAL, PSYCHOLOGICAL_CONDITIONS } from 'config/formSections/psychological';

import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Textarea from 'components/Form/Textarea';
import Accordion from 'components/Form/Accordion';
import { PsychologicalExistingConditionsModel } from 'forms/sections/psychological/existingConditions/types';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Diagnosis from '../Diagnoses/Diagnosis';

const sectionConfig = {
  key: PSYCHOLOGICAL_CONDITIONS.key,
  section: PSYCHOLOGICAL.name,
  store: PSYCHOLOGICAL.store,
  subsection: PSYCHOLOGICAL_CONDITIONS.name,
  storeKey: PSYCHOLOGICAL_CONDITIONS.storeKey,
};

// TODO: Once Subsection has support or this is removed from inheriting
// subsection, we can use props.
interface Props extends Partial<PsychologicalExistingConditionsModel> {
  required?: boolean
  scrollIntoView?: boolean
  formType?: string
}

export class ExistingConditions extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
  }

  update = (queue: Partial<PsychologicalExistingConditionsModel>) => {
    this.props.onUpdate(this.storeKey, {
      HasCondition: this.props.HasCondition,
      ReceivedTreatment: this.props.ReceivedTreatment,
      Explanation: this.props.Explanation,
      TreatmentList: this.props.TreatmentList,
      DidNotFollow: this.props.DidNotFollow,
      DidNotFollowExplanation: this.props.DidNotFollowExplanation,
      Comments: this.props.Comments,
      ReceivedTreatmentComments: this.props.ReceivedTreatmentComments,
      ReceivedTreatmentSummaryComments: this.props.ReceivedTreatmentSummaryComments,
      DidNotFollowComments: this.props.DidNotFollowComments,
      DidNotFollowExplanationComments: this.props.DidNotFollowExplanationComments,
      DidNotReceiveTreatmentComments: this.props.DidNotReceiveTreatmentComments,
      HasPsychosis: this.props.HasPsychosis,
      HasManic: this.props.HasManic,
      HasImpulsive: this.props.HasImpulsive,
      HasPlanToHurt: this.props.HasPlanToHurt,
      ...queue,
    });
  };

  updateHasCondition = (values: PsychologicalExistingConditionsModel['HasCondition']) => {
    this.update({
      HasCondition: values,
      ReceivedTreatment: values.value === 'Yes' ? this.props.ReceivedTreatment : {},
      Explanation: values.value === 'Yes' ? this.props.Explanation : {},
      TreatmentList: values.value === 'Yes' ? this.props.TreatmentList : { items: [], branch: {} },
      DidNotFollow: values.value === 'Yes' ? this.props.DidNotFollow : {},
      DidNotFollowExplanation: values.value === 'Yes' ? this.props.DidNotFollowExplanation : {},
      ReceivedTreatmentComments: values.value === 'Yes' ? this.props.ReceivedTreatmentComments : {},
      ReceivedTreatmentSummaryComments: values.value === 'Yes' ? this.props.ReceivedTreatmentSummaryComments : {},
      DidNotFollowComments: values.value === 'Yes' ? this.props.DidNotFollowComments : {},
      DidNotFollowExplanationComments: values.value === 'Yes' ? this.props.DidNotFollowExplanationComments : {},
      DidNotReceiveTreatmentComments: values.value === 'Yes' ? this.props.DidNotReceiveTreatmentComments : {},
    });
  };

  updateHasPsychosis = (values: PsychologicalExistingConditionsModel['HasPsychosis']) => {
    const noExistingConditions = values?.value === 'No' && this.props.HasManic.value === 'No' && this.props.HasImpulsive.value === 'No' && this.props.HasPlanToHurt.value === 'No';
    this.update({
      HasPsychosis: values,
      ReceivedTreatment: noExistingConditions ? {} : this.props.ReceivedTreatment,
    });
  };

  updateHasManic = (values: PsychologicalExistingConditionsModel['HasManic']) => {
    const noExistingConditions = values?.value === 'No' && this.props.HasPsychosis.value === 'No' && this.props.HasImpulsive.value === 'No' && this.props.HasPlanToHurt.value === 'No';
    this.update({
      HasManic: values,
      ReceivedTreatment: noExistingConditions ? {} : this.props.ReceivedTreatment,
    });
  };

  updateHasImpulsive = (values: PsychologicalExistingConditionsModel['HasImpulsive']) => {
    const noExistingConditions = values?.value === 'No' && this.props.HasManic.value === 'No' && this.props.HasPsychosis.value === 'No' && this.props.HasPlanToHurt.value === 'No';
    this.update({
      HasImpulsive: values,
      ReceivedTreatment: noExistingConditions ? {} : this.props.ReceivedTreatment,
    });
  };

  updateHasPlanToHurt = (values: PsychologicalExistingConditionsModel['HasPlanToHurt']) => {
    const noExistingConditions = values?.value === 'No' && this.props.HasManic.value === 'No' && this.props.HasImpulsive.value === 'No' && this.props.HasPsychosis.value === 'No';
    this.update({
      HasPlanToHurt: values,
      ReceivedTreatment: noExistingConditions ? {} : this.props.ReceivedTreatment,
    });
  };

  updateReceivedTreatment = (
    checkbox: PsychologicalExistingConditionsModel['ReceivedTreatment']
  ) => {
    this.update({
      ReceivedTreatment: checkbox,
      Explanation: checkbox.value === 'No' ? this.props.Explanation : {},
    });
  };

  updateTreatmentList = (values: PsychologicalExistingConditionsModel['TreatmentList']) => {
    this.update({
      TreatmentList: values,
    });
  };

  updateDidNotFollow = (values: PsychologicalExistingConditionsModel['DidNotFollow']) => {
    this.update({
      DidNotFollow: values,
      DidNotFollowExplanation: values.value === 'Yes' ? this.props.DidNotFollowExplanation : {},
    });
  };

  updateExplanation = (values: PsychologicalExistingConditionsModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateDidNotFollowExplanation = (
    values: PsychologicalExistingConditionsModel['DidNotFollowExplanation']
  ) => {
    this.update({
      DidNotFollowExplanation: values,
    });
  };

  updateComments = (values: PsychologicalExistingConditionsModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateReceivedTreatmentComments = (values: PsychologicalExistingConditionsModel['ReceivedTreatmentComments']) => {
    this.update({
      ReceivedTreatmentComments: values,
    });
  };

  updateReceivedTreatmentSummaryComments = (values: PsychologicalExistingConditionsModel['ReceivedTreatmentSummaryComments']) => {
    this.update({
      ReceivedTreatmentSummaryComments: values,
    });
  };

  updateDidNotFollowComments = (values: PsychologicalExistingConditionsModel['DidNotFollowComments']) => {
    this.update({
      DidNotFollowComments: values,
    });
  };

  updateDidNotFollowExplanationComments = (values: PsychologicalExistingConditionsModel['DidNotFollowExplanationComments']) => {
    this.update({
      DidNotFollowExplanationComments: values,
    });
  };

  updateDidNotReceiveTreatmentComments = (values: PsychologicalExistingConditionsModel['DidNotReceiveTreatmentComments']) => {
    this.update({
      DidNotReceiveTreatmentComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Diagnosis || {};
    const treatmentDate = o.Diagnosed || {};
    const date = DateSummary(treatmentDate);
    const condition = (o.Condition || {}).value || '';

    return Summary({
      type: i18n.t('psychological.existingConditions.treatment.collection.itemType'),
      index,
      left: condition,
      right: date,
      placeholder: i18n.t('psychological.existingConditions.treatment.collection.summary'),
    });
  };

  render() {
    const { errors } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('TreatmentList.accordion') === 0);

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div
        className="section-content existingconditions"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_CONDITIONS.key}
      >
        <h1 className="section-header">{i18n.t('psychological.pvq.destination.existingConditions')}</h1>
        <h4 className="section-header">{i18n.t('psychological.pvq.existingConditions.heading.intro')}</h4>
        <Branch
          attribute="HasPsychosis"
          name="haspsychosis"
          label={i18n.t('psychological.pvq.existingConditions.heading.psychosis')}
          labelSize="h4"
          className="eapp-field-wrap hascondition"
          {...this.props.HasPsychosis}
          warning
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onUpdate={this.updateHasPsychosis}
        />
        <Branch
          attribute="HasManic"
          name="hasmanic"
          label={i18n.t('psychological.pvq.existingConditions.heading.manic')}
          labelSize="h4"
          className="eapp-field-wrap hascondition"
          {...this.props.HasManic}
          warning
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onUpdate={this.updateHasManic}
        />
        <Branch
          attribute="HasImpulsive"
          name="hasimpulsive"
          label={i18n.t('psychological.pvq.existingConditions.heading.impulsive')}
          labelSize="h4"
          className="eapp-field-wrap hascondition"
          {...this.props.HasImpulsive}
          warning
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onUpdate={this.updateHasImpulsive}
        />
        <Branch
          attribute="HasPlanToHurt"
          name="hasplantohurt"
          label={i18n.t('psychological.pvq.existingConditions.heading.planToHurt')}
          labelSize="h4"
          className="eapp-field-wrap hascondition"
          {...this.props.HasPlanToHurt}
          warning
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onUpdate={this.updateHasPlanToHurt}
        />
        {(this.props.HasPsychosis?.value === 'Yes'
          || this.props.HasManic?.value === 'Yes'
          || this.props.HasImpulsive?.value === 'Yes'
          || this.props.HasPlanToHurt?.value === 'Yes'
        )
        && (
          <>
            <Field
              attribute="ReceivedTreatment"
              title={i18n.t('psychological.pvq.existingConditions.heading.receivedTreatment')}
              titleSize="h4"
              className={this.props.ReceivedTreatment === 'No' ? 'no-margin-bottom' : ''}
              adjustFor="button"
              scrollIntoView={this.props.scrollIntoView}
            >
              <RadioGroup
                attribute="ReceivedTreatment"
                className="treatment-list option-list"
                selectedValue={(this.props.ReceivedTreatment || {}).value}
                onError={this.handleError}
                required={this.props.required}
              >
                <Radio
                  name="treatment"
                  className="treatment yes"
                  label={i18n.t('psychological.pvq.existingConditions.receivedTreatment.label.yes')}
                  value="Yes"
                  onUpdate={this.updateReceivedTreatment}
                  onError={this.handleError}
                />
                <Radio
                  name="treatment"
                  className="treatment no"
                  label={i18n.t('psychological.pvq.existingConditions.receivedTreatment.label.no')}
                  value="No"
                  onUpdate={this.updateReceivedTreatment}
                  onError={this.handleError}
                />
              </RadioGroup>
            </Field>
            {this.props.ReceivedTreatment.value === 'Yes'
              && (
                <Accordion
                  attribute="TreatmentList"
                  defaultState={this.props.defaultState}
                  {...this.props.TreatmentList}
                  onUpdate={this.updateTreatmentList}
                  summary={this.summary}
                  onError={this.handleError}
                  errors={accordionErrors}
                  description={i18n.t(
                    'psychological.pvq.existingConditions.treatment.collection.description'
                  )}
                  appendTitle={i18n.m(
                    'psychological.pvq.existingConditions.treatment.collection.appendTitle'
                  )}
                  appendLabel={i18n.t(
                    'psychological.pvq.existingConditions.treatment.collection.appendLabel'
                  )}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Diagnosis
                    attribute="TreatmentList"
                    name="Item"
                    prefix="existingConditions.diagnosis"
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    bind
                    formType={this.props.formType}
                  />
                </Accordion>
              )}
          </>
        )}

        <Show when={this.props.HasCondition.value === 'Yes'}>
          <div />
        </Show>
      </div>
    ) : (
      <div
        className="section-content existingconditions"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_CONDITIONS.key}
      >
        <h1 className="section-header">{i18n.t('psychological.destination.existingConditions')}</h1>
        <Branch
          attribute="HasCondition"
          name="hascondition"
          label={i18n.t('psychological.existingConditions.heading.hasCondition')}
          labelSize="h4"
          className="eapp-field-wrap hascondition"
          {...this.props.HasCondition}
          warning
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onUpdate={this.updateHasCondition}
        >
          {i18n.t('psychological.existingConditions.para.hasCondition')}
        </Branch>
        <Comments
          title={i18n.t('psychological.existingConditions.comments')}
          name="Comments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={this.props.HasCondition.value === 'Yes'}>
          <div>
            <Field
              attribute="ReceivedTreatment"
              title={i18n.t('psychological.existingConditions.heading.receivedTreatment')}
              titleSize="h4"
              className={this.props.ReceivedTreatment === 'No' ? 'no-margin-bottom' : ''}
              adjustFor="button"
              scrollIntoView={this.props.scrollIntoView}
            >
              {i18n.t('psychological.existingConditions.para.receivedTreatment')}
              <RadioGroup
                attribute="ReceivedTreatment"
                className="treatment-list option-list"
                selectedValue={(this.props.ReceivedTreatment || {}).value}
                onError={this.handleError}
                required={this.props.required}
              >
                <Radio
                  name="treatment"
                  className="treatment yes"
                  label={i18n.t('psychological.existingConditions.receivedTreatment.label.yes')}
                  value="Yes"
                  onUpdate={this.updateReceivedTreatment}
                  onError={this.handleError}
                />
                <Radio
                  name="treatment"
                  className="treatment no"
                  label={i18n.t('psychological.existingConditions.receivedTreatment.label.no')}
                  value="No"
                  onUpdate={this.updateReceivedTreatment}
                  onError={this.handleError}
                />
                <Radio
                  name="treatment"
                  className="treatment decline"
                  label={i18n.t('psychological.existingConditions.receivedTreatment.label.decline')}
                  value="Decline"
                  onUpdate={this.updateReceivedTreatment}
                  onError={this.handleError}
                />
              </RadioGroup>
            </Field>
            <Comments
              title={i18n.t('psychological.existingConditions.receivedTreatmentComments')}
              name="ReceivedTreatmentComments"
              className="optional-comments"
              attribute="ReceivedTreatmentComments"
              {...this.props.ReceivedTreatmentComments}
              onUpdate={this.updateReceivedTreatmentComments}
              required={false}
            />
            <Show when={this.props.ReceivedTreatment.value === 'No'}>
              <Field
                attribute="Explanation"
                title={i18n.t('psychological.existingConditions.heading.explanation')}
                titleSize="label"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  attribute="Explanation"
                  name="Explanation"
                  className="explanation existing-condition-explanation"
                  {...this.props.Explanation}
                  onUpdate={this.updateExplanation}
                  onError={this.handleError}
                  required={this.props.required}
                />
              </Field>
              <Comments
                title={i18n.t('psychological.existingConditions.didNotReceiveTreatmentComments')}
                name="DidNotReceiveTreatmentComments"
                className="optional-comments"
                attribute="DidNotReceiveTreatmentComments"
                {...this.props.DidNotReceiveTreatmentComments}
                onUpdate={this.updateDidNotReceiveTreatmentComments}
                required={false}
              />
            </Show>

            {this.props.ReceivedTreatment.value === 'Yes'
              && (
                <><Accordion
                  attribute="TreatmentList"
                  defaultState={this.props.defaultState}
                  {...this.props.TreatmentList}
                  onUpdate={this.updateTreatmentList}
                  summary={this.summary}
                  onError={this.handleError}
                  errors={accordionErrors}
                  description={i18n.t(
                    'psychological.existingConditions.treatment.collection.description'
                  )}
                  appendTitle={i18n.t(
                    'psychological.existingConditions.treatment.collection.appendTitle'
                  )}
                  appendLabel={i18n.t(
                    'psychological.existingConditions.treatment.collection.appendLabel'
                  )}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Diagnosis
                    attribute="TreatmentList"
                    name="Item"
                    prefix="existingConditions.diagnosis"
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    bind
                  />
                </Accordion>
                  <Comments
                    title={i18n.t('psychological.existingConditions.receivedTreatmentSummaryComments')}
                    name="ReceivedTreatmentSummaryComments"
                    className="optional-comments"
                    attribute="ReceivedTreatmentSummaryComments"
                    {...this.props.ReceivedTreatmentSummaryComments}
                    onUpdate={this.updateReceivedTreatmentSummaryComments}
                    required={false}
                  />
                </>
              )}
            {this.props.ReceivedTreatment.value === 'Yes' // Answering yes requires at least one entry to be made
              && (
                <><Branch
                  attribute="DidNotFollow"
                  name="didNotFollow"
                  label={i18n.t('psychological.existingConditions.heading.didNotFollow')}
                  labelSize="h4"
                  className="eapp-field-wrap didnotfollow no-margin-bottom"
                  {...this.props.DidNotFollow}
                  onError={this.handleError}
                  required={this.props.required}
                  onUpdate={this.updateDidNotFollow}
                  scrollIntoView={this.props.scrollIntoView}
                />
                  <Comments
                    title={i18n.t('psychological.existingConditions.didNotFollowComments')}
                    name="DidNotFollowComments"
                    className="optional-comments"
                    attribute="DidNotFollowComments"
                    {...this.props.DidNotFollowComments}
                    onUpdate={this.updateDidNotFollowComments}
                    required={false}
                  />
                </>
              )}
            {this.props.ReceivedTreatment.value === 'Yes' && this.props.DidNotFollow.value === 'Yes'
              && (
                <><Field
                  attribute="DidNotFollowExplanation"
                  title={i18n.t('psychological.existingConditions.heading.didNotFollowExplanation')}
                  titleSize="label"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Textarea
                    attribute="DidNotFollowExplanation"
                    name="DidNotFollowExplanation"
                    className="explanation existing-condition-didnotfollow-explanation"
                    {...this.props.DidNotFollowExplanation}
                    onUpdate={this.updateDidNotFollowExplanation}
                    onError={this.handleError}
                    required={this.props.required}
                  />
                </Field>
                  <Comments
                    title={i18n.t('psychological.existingConditions.explanationComments')}
                    name="DidNotFollowExplanationComments"
                    className="optional-comments"
                    attribute="DidNotFollowExplanationComments"
                    {...this.props.DidNotFollowExplanationComments}
                    onUpdate={this.updateDidNotFollowExplanationComments}
                    required={false}
                  />
                </>
              )}

          </div>
        </Show>
      </div>
    );
  }
}

ExistingConditions.defaultProps = {
  HasCondition: {},
  HasPsychosis: {},
  HasManic: {},
  HasImpulsive: {},
  HasPlanToHurt: {},
  Comments: {},
  ReceivedTreatmentComments: {},
  ReceivedTreatmentSummaryComments: {},
  DidNotFollowComments: {},
  DidNotFollowExplanationComments: {},
  DidNotReceiveTreatmentComments: {},
  DidNotFollow: {},

  ReceivedTreatment: {},
  TreatmentList: [],
  defaultState: true,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  scrollToBottom: '.bottom-btns',
  formType: '',

};

export default connectSubsection(ExistingConditions, sectionConfig);
